import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { bankAccountsApi } from 'src/Services/API';
import { BankAccountDto, UpdateBankAccountDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type UpdateBankAccountParams = {
  bankAccountId: string;
  updateData: UpdateBankAccountDto;
};

export const useUpdateBankAccount = (
  options?: UseMutationOptions<BankAccountDto, unknown, UpdateBankAccountParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<BankAccountDto, unknown, UpdateBankAccountParams>(
    ['update-bank-account', assetId],
    async ({ bankAccountId, updateData }: UpdateBankAccountParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await bankAccountsApi.bankAccountsControllerUpdate(
        bankAccountId,
        assetId,
        updateData
      );
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['fetch-bank-accounts', assetId]);
        queryClient.invalidateQueries(['fetch-bank-account', variables.bankAccountId]);
        options?.onSuccess?.(data, variables, context);
        trackEvent('BankAccount', 'update', data.name);
      },
    }
  );
};
